import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { env } from '@/core/env/env';

const REDACTED = '[Redacted]';
const SENSITIVE_HEADER_NAMES = new Set(['authorization', 'cookie', 'x-api-key']);

// Query params are an allow-list, not a deny-list of secret-sounding names: a deny-list misses
// data this app puts in the URL (the users screen's search yields `/users?q=jane.doe@customer.com`),
// and Sentry populates `request.url` from `location.href`, shipping it to a third party. Everything
// not named here is redacted; widen deliberately, never by reflex.
const SAFE_QUERY_PARAMS = new Set(['page', 'pageSize', 'perPage', 'sort', 'order', 'dir', 'limit']);

/** `extra` is caller-supplied; only these keys are known to be PII-free. */
const SAFE_EXTRA_KEYS = new Set(['traceId', 'errorKind']);

/** Base for resolving relative URLs; never emitted, only used to parse. */
const RELATIVE_BASE = 'https://relative.invalid';

function scrubHeaders(headers: Record<string, string>): Record<string, string> {
  const scrubbed: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    scrubbed[name] = SENSITIVE_HEADER_NAMES.has(name.toLowerCase()) ? REDACTED : value;
  }
  return scrubbed;
}

function scrubUrl(url: string): string {
  // Relative URLs must resolve against a base, or `new URL()` throws and a catch-and-return
  // path would let every relative URL — what breadcrumbs carry — escape unscrubbed.
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
  try {
    const parsed = new URL(url, RELATIVE_BASE);
    let redactedAny = false;
    // Snapshot the keys: mutating searchParams while iterating it is UB.
    for (const key of [...parsed.searchParams.keys()]) {
      if (SAFE_QUERY_PARAMS.has(key)) continue;
      parsed.searchParams.set(key, REDACTED);
      redactedAny = true;
    }
    if (!redactedAny) return url;
    return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    // Unparseable even against a base; returning it verbatim would defeat the point, so drop
    // it entirely.
    return REDACTED;
  }
}

/**
 * Scrubs PII before an event leaves the browser: an admin platform's error payloads are exactly
 * where customer data and bearer tokens leak into a third-party service. Exported standalone,
 * independent of `Sentry.init`, so it is unit-testable against a synthetic event without a DSN.
 */
export function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const request = event.request;
  if (request) {
    if (request.headers) request.headers = scrubHeaders(request.headers);
    if (request.url) request.url = scrubUrl(request.url);
    // The body can carry anything a form submitted, including credentials, so drop it
    // outright rather than trying to allow-list safe fields.
    if (request.data !== undefined) request.data = REDACTED;
    if (request.cookies) request.cookies = {};
  }

  // `extra` is whatever a caller passed to reportError; allow-listed for the same reason as
  // query params — a future call site passing a user record would otherwise leak it silently.
  if (event.extra) {
    event.extra = Object.fromEntries(
      Object.entries(event.extra).map(([key, value]) => [
        key,
        SAFE_EXTRA_KEYS.has(key) ? value : REDACTED,
      ]),
    );
  }

  return event;
}

/**
 * Breadcrumbs are a second, quieter export path: `beforeSend` only sees `event.request`, but the
 * browser SDK's default integrations separately attach fetch/xhr (full URL with query string),
 * history (from/to URLs), and console breadcrumbs to every event. Scrubbing the request URL while
 * leaving these alone would ship `?q=<customer email>` anyway.
 */
export function beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  // Console output routinely stringifies whole records with no reliable way to scrub, so drop
  // the category outright.
  if (breadcrumb.category === 'console') return null;

  const data = breadcrumb.data;
  if (!data) return breadcrumb;

  // Sentry types `Breadcrumb.data` as `{ [key: string]: any }`; widening to `unknown` here lets
  // typeof narrow it instead of an `any` defeating the type-checked lint rules below.
  const scrubbed: Record<string, unknown> = { ...data };
  for (const key of ['url', 'from', 'to']) {
    const value: unknown = scrubbed[key];
    if (typeof value === 'string') scrubbed[key] = scrubUrl(value);
  }
  return { ...breadcrumb, data: scrubbed };
}

/**
 * The DSN Sentry may use, or `undefined` to keep it off: a malformed DSN leaves the SDK enabled
 * but undeliverable and logs the raw value, so anything but `https://<key>@<host>/<id>` is off.
 */
export function resolveSentryDsn(raw: string | undefined): string | undefined {
  const dsn = raw?.trim();
  if (!dsn) return undefined;

  let url: URL;
  try {
    url = new URL(dsn);
  } catch {
    return undefined;
  }
  // https only: the CSP's `connect-src` allows no plain-http origin, so an http DSN could not send.
  if (url.protocol !== 'https:') return undefined;
  // Sentry's own parser takes only word characters in the key.
  if (!/^\w+$/.test(url.username) || !url.hostname) return undefined;

  // Project id 0 only appears in Sentry's documentation placeholder.
  const projectId = url.pathname.split('/').pop() ?? '';
  return /^[1-9]\d*$/.test(projectId) ? dsn : undefined;
}

/**
 * Sentry init, called once at boot. Disabling on a missing or invalid DSN is what makes every
 * other call site (`report-error.ts`, `RootErrorBoundary`) unconditional: the SDK is fully wired
 * but inert — it still catches and renders via `Sentry.ErrorBoundary`, it just never talks to
 * the network. Synchronous and side-effect-only, so it slots into `main.tsx`'s boot sequence.
 */
export function initSentry(): void {
  const dsn = resolveSentryDsn(env.VITE_SENTRY_DSN);
  // A value that is set but unusable is a misconfiguration, not an opt-out; say so once rather
  // than lose error reporting silently. The value itself is not logged.
  if (dsn === undefined && env.VITE_SENTRY_DSN?.trim()) {
    console.warn('[sentry] VITE_SENTRY_DSN is not a valid DSN; error reporting is disabled.');
  }

  Sentry.init({
    dsn,
    enabled: dsn !== undefined,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    beforeSend,
    beforeBreadcrumb,
  });
}
